import { useMemo } from "react";
import { getDateOffset } from "@/lib/datetime-utils.js";
import {
  calculateDowntimeHoursInRange,
  sameDowntimeAssetPk,
} from "@/lib/downtime-data-utils.js";
import { removeCommasAndSpaces } from "@/lib/formatting-utils.js";

/**
 * Custom hook to calculate KPI metrics for an asset
 * @param {Object} compressor - The compressor/asset object
 * @param {Array} downtimeEvents - Array of downtime events
 * @param {Array} allParameters - Array of all tag parameters
 * @returns {Object} KPI metrics including runtime, mechanicalAvailability, downtimeEvents, and activeAlarms
 */
export function useKpiMetrics(compressor, downtimeEvents, allParameters) {
  return useMemo(() => {
    if (!compressor || !downtimeEvents) {
      return {
        runtime: 0,
        mechanicalAvailability: 0,
        downtimeEvents: 0,
        activeAlarms: { total: 0, danger: 0, warned: 0 },
      };
    }

    if (compressor.id == null || compressor.id === "") {
      console.warn(
        "[useKpiMetrics] compressor.id (fleet asset primary key) is required to join downtime summaries by numeric assetId."
      );
    }

    const assetDowntime = downtimeEvents.find((dt) =>
      sameDowntimeAssetPk(dt.assetId, compressor.id)
    );

    // Calculate runtime percentage for last 30 days
    const endDate = new Date();
    const startDate = getDateOffset(endDate.toISOString(), "30d");
    const totalHours30Days = 30 * 24;

    let totalDowntimeHours = 0;
    let mechanicalDowntimeHours = 0;
    let eventCount = 0;

    if (assetDowntime && assetDowntime.events) {
      assetDowntime.events.forEach((event) => {
        const hours = calculateDowntimeHoursInRange(event, startDate, endDate);
        totalDowntimeHours += hours;
        if (event.mechanical === true) {
          mechanicalDowntimeHours += hours;
        }
        if (hours > 0) {
          eventCount++;
        }
      });
    }

    const runtimePercentage =
      totalHours30Days > 0
        ? ((totalHours30Days - totalDowntimeHours) / totalHours30Days) * 100
        : 0;

    const mechanicalAvailabilityPercentage =
      totalHours30Days > 0
        ? ((totalHours30Days - mechanicalDowntimeHours) / totalHours30Days)
          * 100
        : 0;

    // Count active alarms from parameters
    let dangerCount = 0;
    let warnedCount = 0;
    allParameters.forEach((param) => {
      if (param.displayType === "Gauge") {
        const currentValue = parseFloat(
          removeCommasAndSpaces(param.currentValue)
        );
        if (!isNaN(currentValue) && isFinite(currentValue)) {
          const { lolo, lo, hi, hihi } = param.alarmSetPoints;
          if (currentValue >= hihi || currentValue <= lolo) {
            dangerCount++;
          } else if (currentValue >= hi || currentValue <= lo) {
            warnedCount++;
          }
        }
      }
    });

    return {
      runtime: Math.max(0, Math.min(100, runtimePercentage)),
      mechanicalAvailability: Math.max(
        0,
        Math.min(100, mechanicalAvailabilityPercentage)
      ),
      downtimeEvents: assetDowntime?.eventCount || eventCount,
      activeAlarms: {
        total: dangerCount + warnedCount,
        danger: dangerCount,
        warned: warnedCount,
      },
    };
  }, [compressor, downtimeEvents, allParameters]);
}
